import { FormattedMessage, FormattedRelativeTime, useIntl } from "react-intl";

import { Tag, Tooltip } from "antd";

import { SyncRun, SyncRunStatus, SyncTrigger } from "@app/models/SyncAction.ts";

const STATUS_COLORS: Record<SyncRunStatus, string> = {
  RUNNING: "processing",
  SUCCESS: "success",
  FAILURE: "error",
};

const TRIGGER_COLORS: Record<SyncTrigger, string> = {
  MANUAL: "purple",
  SERVICE_ACCOUNT: "cyan",
  SCHEDULED: "orange",
};

export const CmtSyncRunStatusTag = ({ status }: { status: SyncRunStatus }) => (
  <Tag color={STATUS_COLORS[status]}>
    {status === "RUNNING" && (
      <FormattedMessage id="settings.status.running" defaultMessage="Running" />
    )}
    {status === "SUCCESS" && (
      <FormattedMessage id="settings.status.success" defaultMessage="Success" />
    )}
    {status === "FAILURE" && (
      <FormattedMessage id="settings.status.failure" defaultMessage="Failure" />
    )}
  </Tag>
);

export const CmtSyncTriggerTag = ({ trigger }: { trigger: SyncTrigger }) => (
  <Tag color={TRIGGER_COLORS[trigger]}>
    {trigger === "MANUAL" && (
      <FormattedMessage id="settings.trigger.manual" defaultMessage="Manual" />
    )}
    {trigger === "SERVICE_ACCOUNT" && (
      <FormattedMessage
        id="settings.trigger.service_account"
        defaultMessage="Service account"
      />
    )}
    {trigger === "SCHEDULED" && (
      <FormattedMessage
        id="settings.trigger.scheduled"
        defaultMessage="Automatic"
      />
    )}
  </Tag>
);

export const CmtSyncRunAuthor = ({ run }: { run: SyncRun }) =>
  run.trigger === "SCHEDULED" ? (
    <FormattedMessage
      id="settings.trigger.scheduled"
      defaultMessage="Automatic"
    />
  ) : (
    <>{run.triggeredByName ?? run.triggeredBy}</>
  );

export const CmtSyncRunStartedAt = ({ run }: { run: SyncRun }) => {
  const intl = useIntl();
  const startedAt = new Date(run.startedAt);

  return (
    <Tooltip
      title={intl.formatDate(startedAt, {
        dateStyle: "full",
        timeStyle: "medium",
      })}
    >
      <span>
        <FormattedRelativeTime
          value={Math.round((startedAt.getTime() - Date.now()) / 1000)}
          numeric="auto"
          updateIntervalInSeconds={10}
        />
      </span>
    </Tooltip>
  );
};

const formatDuration = (milliseconds: number) => {
  const totalSeconds = Math.max(0, Math.round(milliseconds / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours} h ${minutes} min`;
  }
  if (minutes > 0) {
    return `${minutes} min ${seconds} s`;
  }
  return `${seconds} s`;
};

export const CmtSyncRunDuration = ({ run }: { run: SyncRun }) =>
  run.endedAt ? (
    <>
      {formatDuration(
        new Date(run.endedAt).getTime() - new Date(run.startedAt).getTime(),
      )}
    </>
  ) : (
    <>-</>
  );
